import React from "react";

import { Check, X } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const getPlanCardCopy = (
  product:
    | "general"
    | "replo-builder"
    | "shopify-theme-editor"
    | "analytics"
    | "ab-testing",
  tier: "starter" | "pro",
): React.ReactNode[] => {
  const sharedClassName = "text-xs font-normal";

  const createFeatureItem = (
    key: string,
    content: React.ReactNode,
    icon: React.ReactNode = <Check size={16} className="shrink-0" />,
  ): React.ReactNode => {
    return (
      <div key={key} className="flex items-center gap-3">
        {icon}
        <span className={sharedClassName}>{content}</span>
      </div>
    );
  };

  const productKey = `${product}-${tier}` as const;

  return exhaustiveSwitch({ type: productKey })({
    "general-starter": () => [
      createFeatureItem("0", "Priority chat support"),
      createFeatureItem("1", "Full template library access"),
      createFeatureItem("2", "Unlimited team members"),
      createFeatureItem(
        "3",
        <>
          <span className="text-placeholder">Dedicated Slack channel</span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
      createFeatureItem(
        "4",
        <>
          <span className="text-placeholder">White-glove services</span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
      createFeatureItem(
        "5",
        <>
          <span className="text-placeholder">Bulk page generation</span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
      createFeatureItem(
        "6",
        <>
          <span className="text-placeholder">Dedicated account manager</span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
      createFeatureItem(
        "7",
        <>
          <span className="text-placeholder">1:1 onboarding & training</span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
    ],
    "general-pro": () => [
      createFeatureItem("0", "Priority chat support"),
      createFeatureItem("1", "Full template library access"),
      createFeatureItem("2", "Unlimited team members"),
      createFeatureItem("3", "Dedicated Slack channel"),
      createFeatureItem("4", "White-glove services"),
      createFeatureItem("5", "Bulk page generation"),
      createFeatureItem("6", "Dedicated account manager"),
      createFeatureItem("7", "1:1 onboarding & training"),
    ],
    "replo-builder-starter": () => [
      createFeatureItem(
        "0",
        <>
          <span className="font-bold">400 AI chats</span> per month
        </>,
      ),
      createFeatureItem(
        "1",
        <>
          <span className="font-bold">Unlimited pages</span> with AI generation
        </>,
      ),
      createFeatureItem(
        "2",
        <>
          <span className="font-bold">Custom domains</span> included
        </>,
      ),
      createFeatureItem(
        "3",
        <>
          <span className="font-bold">100K visitor sessions</span> included,
          then <span className="font-bold">$1.50</span>/1k
        </>,
      ),
      createFeatureItem("4", "No Replo branding on your pages"),
      createFeatureItem("5", "SEO optimization built-in"),
      createFeatureItem(
        "6",
        <>
          <span className="text-placeholder">Volume session discounts</span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
      createFeatureItem(
        "7",
        <>
          <span className="text-placeholder">
            AI landing pages from ad creatives
          </span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
      createFeatureItem(
        "8",
        <>
          <span className="text-placeholder">
            Lower payment processing fees
          </span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
    ],
    "replo-builder-pro": () => [
      createFeatureItem(
        "0",
        <>
          <span className="font-bold">Unlimited AI chats</span>
        </>,
      ),
      createFeatureItem(
        "1",
        <>
          <span className="font-bold">Unlimited pages</span> with AI generation
        </>,
      ),
      createFeatureItem("2", <span className="font-bold">Custom domains</span>),
      createFeatureItem(
        "3",
        <>
          <span className="font-bold">500K visitor sessions</span> included,
          then <span className="font-bold">$1.00</span>/1k
        </>,
      ),
      createFeatureItem("4", "No Replo branding on your pages"),
      createFeatureItem("5", "SEO optimization built-in"),
      createFeatureItem(
        "6",
        <>
          <span className="font-bold">33% off</span> volume session pricing
        </>,
      ),
      createFeatureItem(
        "7",
        <>
          <span className="font-bold">AI landing pages</span> from ad creatives
        </>,
      ),
      createFeatureItem("8", "Lower payment processing fees"),
    ],
    "shopify-theme-editor-starter": () => [
      createFeatureItem(
        "0",
        <>
          <span className="font-bold">15 published items</span> on Shopify
        </>,
      ),
      createFeatureItem("1", "Unlimited pageviews"),
      createFeatureItem(
        "2",
        <>
          <span className="font-bold">5K</span> A/B test sessions monthly
        </>,
      ),
      createFeatureItem("3", "Drag-and-drop Shopify Theme Builder"),
      createFeatureItem("4", "Conversion-tested template library"),
      createFeatureItem(
        "5",
        "Direct integration with Shopify products & collections",
      ),
      createFeatureItem("6", "AI assistant for faster page building"),
    ],
    "shopify-theme-editor-pro": () => [
      createFeatureItem(
        "0",
        <>
          <span className="font-bold">Unlimited published items</span> on
          Shopify
        </>,
      ),
      createFeatureItem("1", "Unlimited pageviews"),
      createFeatureItem(
        "2",
        <>
          <span className="font-bold">100K</span> A/B test sessions monthly
        </>,
      ),
      createFeatureItem("3", "Visual drag-and-drop Shopify Theme Builder"),
      createFeatureItem("4", "Conversion-tested template library"),
      createFeatureItem("5", "Auto-sync Shopify products & collections"),
      createFeatureItem("6", "AI assistant for faster page building"),
    ],
    "analytics-starter": () => [
      createFeatureItem("0", "Full analytics across all your published pages"),
      createFeatureItem(
        "1",
        "Track pageviews, sessions, conversion rate, AOV & revenue",
      ),
      createFeatureItem(
        "2",
        "Flexible attribution models including fractional",
      ),
      createFeatureItem("3", "Real-time trends and KPI dashboards"),
      createFeatureItem(
        "4",
        <>
          <span className="text-placeholder">
            AI-powered optimization recommendations
          </span>
        </>,
        <X size={16} className="shrink-0 text-rose" />,
      ),
    ],
    "analytics-pro": () => [
      createFeatureItem("0", "Full analytics across all your published pages"),
      createFeatureItem(
        "1",
        "Track pageviews, sessions, conversion rate, AOV & revenue",
      ),
      createFeatureItem(
        "2",
        "Flexible attribution models including fractional",
      ),
      createFeatureItem("3", "Real-time trends and KPI dashboards"),
      createFeatureItem("4", "AI-powered optimization recommendations"),
    ],
    "ab-testing-starter": () => [
      createFeatureItem("0", "Forever links for split-tests"),
      createFeatureItem("1", "Real-time conversion tracking"),
      createFeatureItem("2", "Unlimited test variants"),
      createFeatureItem("3", "Test any page, Replo or external"),
    ],
    "ab-testing-pro": () => [
      createFeatureItem("0", "Forever links for split-tests"),
      createFeatureItem("1", "Real-time conversion tracking"),
      createFeatureItem("2", "Unlimited test variants"),
      createFeatureItem("3", "Test any page, Replo or external"),
    ],
  });
};

export default getPlanCardCopy;
